import { Box, Drawer, IconButton, Stack, Typography, useTheme } from '@mui/material';
import React from 'react';
import { CloseIcon } from '../icons';

interface DrawerBottomProps {
  open: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  title?: string | React.ReactNode;
  height?: string;
  extraAction?: React.ReactNode;
  zIndex?: number;
  /** Enable mobile keyboard-aware positioning */
  mobileKeyboardAware?: boolean;
  /** Footer content that stays fixed at the bottom */
  footer?: React.ReactNode;
}

const DrawerBottom = ({
  open,
  onClose,
  title,
  height,
  children,
  extraAction,
  zIndex,
  mobileKeyboardAware = false,
  footer,
}: DrawerBottomProps) => {
  const theme = useTheme();

  return (
    <Drawer
      anchor={'bottom'}
      open={open}
      onClose={onClose}
      sx={{ zIndex: zIndex || (theme.zIndex.drawer + 1) }}
      ModalProps={{
        keepMounted: false,
      }}
      PaperProps={{
        elevation: 3,
        sx: {
          borderRadius: '16px 16px 0 0',
          height: height || 'auto',
          maxHeight: '90dvh',
          ...(mobileKeyboardAware && {
            '@media (max-width: 640px)': {
              // On mobile, ensure the drawer stays above the keyboard
              position: 'fixed',
              top: 16,
              // Use viewport height minus safe area for mobile
              maxHeight: 'calc(100dvh - env(safe-area-inset-bottom))',
              // Ensure it's above the keyboard
              zIndex: theme.zIndex.drawer + 10,
            },
          }),
        },
      }}
    >
      <Stack
        direction="column"
        height="100%"
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Fixed position header */}
        <Stack
          direction="row"
          alignItems="center"
          px={3}
          py={2}
          sx={{
            top: 0,
            zIndex: 10,
            position: 'sticky',
            backgroundColor: 'background.paper',
          }}
        >
          <IconButton onClick={onClose} edge="start" aria-label="close">
            <CloseIcon style={{ fontSize: 32, color: theme.palette.text.textPrimary }} />
          </IconButton>
          <Box flexGrow={1}></Box>
          {extraAction}
          {!extraAction && <Box width={40}></Box>} {/* Empty space to balance the close button */}
        </Stack>

        <Box px={3} mb={2}>
          <Typography variant="h2" align="left">{title}</Typography>
        </Box>

        {/* Scrollable content */}
        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            p: 3,
            pt: 1,
            ...(mobileKeyboardAware && {
              '@media (max-width: 640px)': {
                // Ensure content is scrollable on mobile
                maxHeight: 'calc(100dvh - 120px)', // Account for header and safe area
                paddingBottom: 'env(safe-area-inset-bottom)',
              },
            }),
          }}
        >
          {children}
        </Box>

        {/* Fixed footer */}
        {footer && (
          <Box
            sx={{
              p: 3,
              pt: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: 'background.paper',
            }}
          >
            {footer}
          </Box>
        )}
      </Stack>
    </Drawer>
  );
};

export default DrawerBottom;